import {
  EnemyState,
  EnemyType,
  Owner,
  PickupKind,
  ProjectileFlags,
  ProjectileKind,
} from '../constants';
import { FORMATION_STATS } from '../formation/formations';
import { circleOverlap, segmentVsCircle, sweptCircleVsCircle } from '../math/collision';
import type { GameWorld } from '../world/GameWorld';
import { DeviceId, NodeLensId } from '../weapons/definitions';

const CELL = 16;
const COLS = 24;
const ROWS = 14;
const CELLS = COLS * ROWS;

export class CollisionWorld {
  private readonly heads = new Int16Array(CELLS);
  private readonly next = new Int16Array(256);

  update(world: GameWorld): void {
    // HOTPATH-BEGIN
    this.buildGrid(world);
    this.playerProjectiles(world);
    this.enemyProjectiles(world);
    this.tether(world);
    this.pickups(world);
    this.contacts(world);
    // HOTPATH-END
  }

  private buildGrid(world: GameWorld): void {
    this.heads.fill(-1);
    this.next.fill(-1);
    const enemies = world.enemies;
    const active = enemies.indices.active;
    for (let i = 0; i < active.length; i += 1) {
      if (active[i] === 0 || enemies.state[i] === EnemyState.Dying) continue;
      const cx = Math.floor(enemies.x[i] / CELL);
      const cy = Math.floor(enemies.y[i] / CELL);
      if (cx < 0 || cx >= COLS || cy < 0 || cy >= ROWS) continue;
      const cell = cy * COLS + cx;
      this.next[i] = this.heads[cell];
      this.heads[cell] = i;
    }
  }

  private playerProjectiles(world: GameWorld): void {
    const projectiles = world.projectiles;
    const active = projectiles.indices.active;
    for (let i = 0; i < active.length; i += 1) {
      if (active[i] === 0 || projectiles.owner[i] !== Owner.Player) continue;
      if ((projectiles.flags[i] & ProjectileFlags.Segment) !== 0) {
        this.playerSegmentVsEnemies(world, i);
        this.playerSegmentVsBoss(world, i);
        continue;
      }
      let hit = false;
      const cx = Math.floor(projectiles.x[i] / CELL);
      const cy = Math.floor(projectiles.y[i] / CELL);
      for (let oy = -1; oy <= 1 && !hit; oy += 1) {
        const row = cy + oy;
        if (row < 0 || row >= ROWS) continue;
        for (let ox = -1; ox <= 1 && !hit; ox += 1) {
          const col = cx + ox;
          if (col < 0 || col >= COLS) continue;
          let enemy = this.heads[row * COLS + col];
          while (enemy >= 0) {
            if (this.hitEnemy(world, i, enemy)) {
              hit = true;
              break;
            }
            enemy = this.next[enemy];
          }
        }
      }
      if (!hit && world.bosses.active) hit = this.hitBossParts(world, i);
      if (hit && active[i] !== 0 && (projectiles.flags[i] & ProjectileFlags.Piercing) === 0) projectiles.release(i);
    }
  }

  private hitEnemy(world: GameWorld, projectile: number, enemy: number): boolean {
    const p = world.projectiles;
    const e = world.enemies;
    if (!sweptCircleVsCircle(p.prevX[projectile], p.prevY[projectile], p.x[projectile], p.y[projectile], p.radius[projectile], e.x[enemy], e.y[enemy], e.radius[enemy])) return false;
    if (e.lastAttackId[enemy] === p.attackId[projectile]) return false;
    const type = e.type[enemy];
    if (type === EnemyType.MirrorWisp && world.player.lens !== NodeLensId.Tether && (p.flags[projectile] & ProjectileFlags.Reflected) === 0 && p.kind[projectile] !== ProjectileKind.Lattice) {
      p.flags[projectile] |= ProjectileFlags.Reflected;
      p.vx[projectile] = -Math.abs(p.vx[projectile]) * 0.75;
      p.vy[projectile] += ((projectile & 1) === 0 ? -1 : 1) * 1.15;
      p.angle[projectile] = Math.atan2(p.vy[projectile], p.vx[projectile]);
      world.emitSfx(6);
      return false;
    }
    e.lastAttackId[enemy] = p.attackId[projectile];
    let damage = p.damage[projectile];
    if (type === EnemyType.PlateFin && p.vx[projectile] > 0 && (p.flags[projectile] & ProjectileFlags.Piercing) === 0) damage *= 0.32;
    if (type === EnemyType.MirrorWisp && world.nodes.tetherActiveTicks > 0) damage *= 1.7;
    e.hp[enemy] -= damage;
    world.particles.spawn(p.x[projectile], p.y[projectile], -0.4, 0, 14, 1.5, 1, 1);
    if (p.kind[projectile] === ProjectileKind.Prism && p.chainDepth[projectile] < 2) world.spawnPrismRefraction(enemy, projectile);
    if (e.hp[enemy] <= 0) world.destroyEnemy(enemy);
    return true;
  }

  private hitBossParts(world: GameWorld, projectile: number): boolean {
    const p = world.projectiles;
    const parts = world.bossParts;
    const active = parts.indices.active;
    for (let i = 0; i < active.length; i += 1) {
      if (active[i] === 0 || parts.visible[i] === 0) continue;
      if (!sweptCircleVsCircle(p.prevX[projectile], p.prevY[projectile], p.x[projectile], p.y[projectile], p.radius[projectile], parts.x[i], parts.y[i], parts.radius[i])) continue;
      if (world.bosses.damagePart(i, p.damage[projectile], p.attackId[projectile], world)) return true;
    }
    return false;
  }

  private playerSegmentVsBoss(world: GameWorld, projectile: number): void {
    if (!world.bosses.active) return;
    const p = world.projectiles;
    const parts = world.bossParts;
    const active = parts.indices.active;
    for (let i = 0; i < active.length; i += 1) {
      if (active[i] === 0 || parts.visible[i] === 0) continue;
      if (segmentVsCircle(p.x[projectile], p.y[projectile], p.x2[projectile], p.y2[projectile], parts.x[i], parts.y[i], parts.radius[i] + p.radius[projectile])) {
        world.bosses.damagePart(i, p.damage[projectile], p.attackId[projectile], world);
      }
    }
  }

  private playerSegmentVsEnemies(world: GameWorld, projectile: number): void {
    const p = world.projectiles;
    const enemies = world.enemies;
    const active = enemies.indices.active;
    const attackId = p.attackId[projectile];
    for (let i = 0; i < active.length; i += 1) {
      if (
        active[i] === 0
        || enemies.state[i] === EnemyState.Dying
        || enemies.lastAttackId[i] === attackId
      ) continue;
      if (!segmentVsCircle(
        p.x[projectile],
        p.y[projectile],
        p.x2[projectile],
        p.y2[projectile],
        enemies.x[i],
        enemies.y[i],
        enemies.radius[i] + p.radius[projectile],
      )) continue;
      enemies.lastAttackId[i] = attackId;
      enemies.hp[i] -= p.damage[projectile];
      world.particles.spawn(enemies.x[i], enemies.y[i], -0.4, 0, 14, 1.5, 1, 1);
      if (enemies.hp[i] <= 0) world.destroyEnemy(i);
    }
  }

  private enemyProjectiles(world: GameWorld): void {
    const p = world.projectiles;
    const active = p.indices.active;
    const player = world.player;
    for (let i = 0; i < active.length; i += 1) {
      if (active[i] === 0 || p.owner[i] !== Owner.Enemy) continue;
      let consumed = false;
      if ((p.flags[i] & ProjectileFlags.Unblockable) === 0 && !world.nodeDefenseDisabled()) {
        for (let n = 0; n < 3; n += 1) {
          if (world.nodes.disabledTicks[n] > 0 || world.nodes.charge[n] <= 0) continue;
          const blockRadius = FORMATION_STATS[world.nodes.formation].blockRadius;
          if (!sweptCircleVsCircle(p.prevX[i], p.prevY[i], p.x[i], p.y[i], p.radius[i], world.nodes.x[n], world.nodes.y[n], blockRadius)) continue;
          if (p.kind[i] === ProjectileKind.Lock) {
            world.nodes.disabledTicks[n] = 150;
            world.nodes.charge[n] = 0;
            p.release(i);
            world.emitSfx(3);
            consumed = true;
            break;
          }
          const canReflect = world.player.lens === NodeLensId.Mirror && ((p.attackId[i] + n) & 3) === 0 && world.nodes.charge[n] >= 0.42;
          world.nodes.charge[n] -= canReflect ? 0.2 : 0.34;
          if (world.nodes.charge[n] <= 0) {
            world.nodes.charge[n] = 0;
            world.nodes.disabledTicks[n] = 72;
          }
          if (canReflect) {
            p.owner[i] = Owner.Player;
            p.kind[i] = ProjectileKind.Reflected;
            p.flags[i] = ProjectileFlags.Reflected;
            p.vx[i] = Math.abs(p.vx[i]) * 1.35 + 0.6;
            p.vy[i] *= -0.55;
            p.damage[i] = 7;
            p.attackId[i] = world.nextAttackId();
            p.life[i] = 160;
          } else {
            p.release(i);
          }
          world.nodes.pulse[n] = 8;
          world.particles.spawn(world.nodes.x[n], world.nodes.y[n], 0, 0, 18, 2, 0, 2);
          world.emitSfx(canReflect ? 6 : 2);
          consumed = true;
          break;
        }
      }
      if (consumed || active[i] === 0 || !player.alive || player.invulnerabilityTicks > 0) continue;
      const hit = (p.flags[i] & ProjectileFlags.Segment) !== 0
        ? segmentVsCircle(p.x[i], p.y[i], p.x2[i], p.y2[i], player.x, player.y, player.radius + p.radius[i])
        : sweptCircleVsCircle(p.prevX[i], p.prevY[i], p.x[i], p.y[i], p.radius[i], player.x, player.y, player.radius);
      if (hit) {
        if ((p.flags[i] & ProjectileFlags.Segment) === 0) p.release(i);
        world.damagePlayer();
      }
    }
  }

  private tether(world: GameWorld): void {
    if (world.nodes.tetherActiveTicks <= 0 || world.simTick % 6 !== 0) return;
    const attackId = world.nextAttackId();
    const enemies = world.enemies;
    const active = enemies.indices.active;
    for (let i = 0; i < active.length; i += 1) {
      if (active[i] === 0 || enemies.state[i] === EnemyState.Dying || enemies.lastAttackId[i] === attackId) continue;
      let hit = false;
      for (let n = 0; n < 3; n += 1) {
        const next = (n + 1) % 3;
        if (segmentVsCircle(world.nodes.x[n], world.nodes.y[n], world.nodes.x[next], world.nodes.y[next], enemies.x[i], enemies.y[i], enemies.radius[i] + 1.5)) { hit = true; break; }
      }
      if (hit) {
        enemies.lastAttackId[i] = attackId;
        enemies.hp[i] -= 7.5;
        if (enemies.hp[i] <= 0) world.destroyEnemy(i);
      }
    }
    if (world.bosses.active) {
      const parts = world.bossParts;
      for (let i = 0; i < parts.indices.active.length; i += 1) {
        if (parts.indices.active[i] === 0) continue;
        for (let n = 0; n < 3; n += 1) {
          const next = (n + 1) % 3;
          if (segmentVsCircle(world.nodes.x[n], world.nodes.y[n], world.nodes.x[next], world.nodes.y[next], parts.x[i], parts.y[i], parts.radius[i])) {
            world.bosses.damagePart(i, 5.5, attackId, world);
            break;
          }
        }
      }
    }
  }

  private pickups(world: GameWorld): void {
    const pickups = world.pickups;
    const active = pickups.indices.active;
    const player = world.player;
    for (let i = 0; i < active.length; i += 1) {
      if (active[i] === 0) continue;
      const dx = player.x - pickups.x[i];
      const dy = player.y - pickups.y[i];
      const distSq = dx * dx + dy * dy;
      if (distSq < 36 * 36 && distSq > 0.01) {
        const inv = 1 / Math.sqrt(distSq);
        pickups.vx[i] += dx * inv * 0.11;
        pickups.vy[i] += dy * inv * 0.11;
      }
      if (!circleOverlap(player.x, player.y, player.radius + 2, pickups.x[i], pickups.y[i], 5)) continue;
      // A dead ship cannot bank weapon progress. Legacy Lux remains collectible
      // while dead exactly as before; only R2 continuous Lux joins this guard.
      if (
        !player.alive
        && (
          pickups.kind[i] === PickupKind.Power
          || (world.continuousArsenalEnabled && pickups.kind[i] === PickupKind.Lux)
        )
      ) continue;
      const value = pickups.value[i];
      if (pickups.kind[i] === PickupKind.Lux) world.grantLux(value);
      else if (pickups.kind[i] === PickupKind.Repair) player.lives = Math.min(player.maxLives, player.lives + value);
      else if (pickups.kind[i] === PickupKind.Power) world.grantPower(1);
      else player.score += value;
      pickups.indices.release(i);
      world.emitSfx(0);
    }
  }

  private contacts(world: GameWorld): void {
    const player = world.player;
    if (!player.alive || player.invulnerabilityTicks > 0) return;
    const enemies = world.enemies;
    const active = enemies.indices.active;
    for (let i = 0; i < active.length; i += 1) {
      if (active[i] === 0 || enemies.state[i] === EnemyState.Dying) continue;
      if (circleOverlap(player.x, player.y, player.radius, enemies.x[i], enemies.y[i], enemies.radius[i] * 0.75)) {
        world.damagePlayer();
        return;
      }
    }
    if (world.bosses.active) {
      const parts = world.bossParts;
      for (let i = 0; i < parts.indices.active.length; i += 1) {
        if (parts.indices.active[i] !== 0 && circleOverlap(player.x, player.y, player.radius, parts.x[i], parts.y[i], parts.radius[i] * 0.8)) {
          world.damagePlayer();
          return;
        }
      }
    }
  }
}
